import { Feature, PlanInterval } from '../../../../models';

export interface TrueBundlePlan {
  feature: Feature;
  productId: string;
  titleId: string;
  tooltipTitleId: string;
  descriptionId: string;
  priceHelpId: string;
  productLabelIds: string[];
  benefitIds: string[];
  /**
   * Starter cards only: the LISTED annual price of the package, i.e. what the same seats cost at
   * list price (design 23.9: True Manual 3 x 900 = $2,700, True Automation 3 x 2,500 = $7,500).
   * Display-only - it feeds the "-45%" badge and nothing else; the checkout is priced by the BE
   * from the eCom add-on prices minus the Starter campaign. LaunchDarkly
   * `subscriptionPlanPrice.<prefix>ListAnnually` overrides it when seeded.
   */
  listPriceAnnually?: number;
}

/**
 * The legacy standalone True SKUs. R6 (Decision 16) retired them from self-serve, so they are no
 * longer rows on the "Add seats & add-on" table — but their definitions stay here because the
 * i18n titles still name EXISTING subscriptions on the Subscription page, Order History and the
 * order summary (`getTrueBundleTitleId`). Never add them back to [TRUE_BUNDLE_PLAN_OPTIONS].
 */
const RETIRED_TRUE_BUNDLE_PLANS: TrueBundlePlan[] = [
  {
    feature: Feature.TRUE_AUTOMATION,
    productId: 'true_automation',
    titleId: 'page.plan.v2.true.automation.title',
    tooltipTitleId: 'page.plan.v2.true.automation.tooltip.title',
    descriptionId: 'page.plan.v2.true.automation.description',
    priceHelpId: 'page.plan.v2.true.automation.price.help',
    productLabelIds: [
      'page.plan.v2.true.product.platform',
      'page.plan.v2.true.product.cloud',
      'page.plan.v2.true.product.studio',
    ],
    benefitIds: [
      'page.plan.v2.true.automation.benefit.studio',
      'page.plan.v2.true.automation.benefit.authoring',
      'page.plan.v2.true.automation.benefit.ai',
      'page.plan.v2.true.automation.benefit.mcp',
      'page.plan.v2.true.automation.benefit.record',
      'page.plan.v2.true.automation.benefit.coverage',
      'page.plan.v2.true.automation.benefit.management',
      'page.plan.v2.true.automation.benefit.viewer',
      'page.plan.v2.true.automation.benefit.parallel',
      'page.plan.v2.true.automation.benefit.live',
    ],
  },
  {
    feature: Feature.TRUE_PLATFORM_ONLY,
    productId: 'true_platform',
    titleId: 'page.plan.v2.true.platform.title',
    tooltipTitleId: 'page.plan.v2.true.platform.tooltip.title',
    descriptionId: 'page.plan.v2.true.platform.description',
    priceHelpId: 'page.plan.v2.true.platform.price.help',
    productLabelIds: [
      'page.plan.v2.true.product.platform',
      'page.plan.v2.true.product.cloud',
    ],
    benefitIds: [
      'page.plan.v2.true.platform.benefit.management',
      'page.plan.v2.true.platform.benefit.ai',
      'page.plan.v2.true.platform.benefit.mcp',
      'page.plan.v2.true.platform.benefit.viewer',
      'page.plan.v2.true.platform.benefit.live',
    ],
  },
];

/**
 * Rows the plan table actually SELLS. R6 (Decision 16) emptied it: True Automation and True
 * Platform are retired from self-serve for every account, new or existing, and the BE answers
 * `legacy.true.sku.retired` on every self-serve route. The Starter bundles take their place.
 */
export const TRUE_BUNDLE_PLAN_OPTIONS: TrueBundlePlan[] = [];

/**
 * The True Bundle Starters sold online (PR 1): one $0 plan whose item-backed add-ons carry the
 * price. They are a SEPARATE array from [TRUE_BUNDLE_PLAN_OPTIONS] - which R6 emptied - so the
 * retirement of True Automation / True Platform stays revertible on its own.
 *
 * A card is rendered only when LaunchDarkly `trueBundleConfig` holds an entry for its feature and
 * that entry is still on sale (`sellUntil`); the components and their quantities come from the
 * same config, never from this file (Decision 3: Product owns the package contents).
 * True Enterprise is direct-sale only and lands with PR 2.
 */
export const TRUE_BUNDLE_STARTER_OPTIONS: TrueBundlePlan[] = [
  {
    feature: Feature.TRUE_BUNDLE_MANUAL,
    productId: 'true_bundle_manual',
    titleId: 'page.plan.v2.true.bundle.manual.title',
    tooltipTitleId: 'page.plan.v2.true.bundle.manual.tooltip.title',
    descriptionId: 'page.plan.v2.true.bundle.manual.description',
    priceHelpId: 'page.plan.v2.true.bundle.starter.price.help',
    productLabelIds: [
      'page.plan.v2.true.product.platform',
      'page.plan.v2.true.product.cloud',
    ],
    benefitIds: [
      'page.plan.v2.true.bundle.manual.benefit.ai',
      'page.plan.v2.true.bundle.manual.benefit.live',
      'page.plan.v2.true.bundle.manual.benefit.management',
      'page.plan.v2.true.bundle.manual.benefit.reports',
      'page.plan.v2.true.bundle.manual.benefit.viewer',
    ],
    listPriceAnnually: 2700,
  },
  {
    feature: Feature.TRUE_BUNDLE_AUTOMATION,
    productId: 'true_bundle_automation',
    titleId: 'page.plan.v2.true.bundle.automation.title',
    tooltipTitleId: 'page.plan.v2.true.bundle.automation.tooltip.title',
    descriptionId: 'page.plan.v2.true.bundle.automation.description',
    priceHelpId: 'page.plan.v2.true.bundle.starter.price.help',
    productLabelIds: [
      'page.plan.v2.true.product.studio',
      'page.plan.v2.true.product.platform',
      'page.plan.v2.true.product.cloud',
    ],
    benefitIds: [
      'page.plan.v2.true.bundle.automation.benefit.manual',
      'page.plan.v2.true.bundle.automation.benefit.studio',
      'page.plan.v2.true.bundle.automation.benefit.platforms',
      'page.plan.v2.true.bundle.automation.benefit.execution',
      'page.plan.v2.true.bundle.automation.benefit.data',
      'page.plan.v2.true.bundle.automation.benefit.viewer',
    ],
    listPriceAnnually: 7500,
  },
];

/** Features of the bundle cards this page can sell (PR 1: the two online Starters). */
export const TRUE_BUNDLE_STARTER_FEATURES: Feature[] = TRUE_BUNDLE_STARTER_OPTIONS.map(
  plan => plan.feature,
);

/**
 * Features whose EXISTING subscriptions still drive this page's seat / free-session maths and the
 * plan naming shared with Order Summary. Deliberately NOT derived from [TRUE_BUNDLE_PLAN_OPTIONS]
 * any more: a customer who bought True Automation before R6 keeps their seats, they are simply not
 * on sale (Decision 16).
 */
export const TRUE_BUNDLE_PURCHASE_FEATURES: Feature[] = RETIRED_TRUE_BUNDLE_PLANS.map(
  plan => plan.feature,
);

/**
 * Katalon Studio Enterprise rendered as the FIRST row of the main plan table (since R6 retired
 * True Automation and True Platform, it is currently the only one). It shares the row layout with
 * the True bundle plans but its seats/interval state and preview requests stay on the "more testing
 * products" path, so it is not part of TRUE_BUNDLE_PLAN_OPTIONS / TRUE_BUNDLE_PURCHASE_FEATURES.
 */
export const KSE_STUDIO_PLAN_ROW: TrueBundlePlan = {
  feature: Feature.PER_USER_KSE,
  productId: 'kse',
  titleId: 'feature.per_user_kse.product.name',
  tooltipTitleId: 'page.plan.v2.true.studio.tooltip.title',
  descriptionId: 'page.plan.v2.true.testing.studio.description',
  priceHelpId: 'page.plan.v2.true.automation.price.help',
  productLabelIds: [
    'page.plan.v2.true.product.studio',
  ],
  benefitIds: [
    'page.plan.v2.true.studio.benefit.authoring',
    'page.plan.v2.true.studio.benefit.ai',
    'page.plan.v2.true.studio.benefit.record',
    'page.plan.v2.true.studio.benefit.coverage',
  ],
  // Katalon Studio card of "Choose your plan" (design 23.9): the list price per seat behind its
  // saving badge ($1,000 Starter seat vs $2,199). Display-only; LD `kseListAnnually` overrides it.
  listPriceAnnually: 2199,
};

export const TRUE_MORE_TESTING_PRODUCT_FEATURES = [
  Feature.PER_USER_KSE,
  Feature.UNLIMITED_ENGINE,
  Feature.TESTCLOUD_SESSION_CROSS_BROWSER,
  Feature.TESTCLOUD_SESSION_MOBILE_APP,
  Feature.TESTCLOUD_SESSION_LIVE_TESTING,
  Feature.TESTOPS_G3,
];

/**
 * The SKU whose cycle query param stands for "the cycle this link asked for" on PlanV2. It used to
 * be True Automation; R6 retired that row and commit 6 parked the default on Katalon Studio
 * Enterprise until the Starters landed — it is now True Manual Starter, the first bundle row.
 */
export const DEFAULT_TRUE_BUNDLE_FEATURE = Feature.TRUE_BUNDLE_MANUAL;

// Looks a plan up across BOTH the on-sale rows and the retired ones, so an existing True
// Automation / True Platform subscription can still be named after R6 removed its row.
export const getTrueBundlePlan = (
  feature?: Feature,
): TrueBundlePlan => [
  ...TRUE_BUNDLE_STARTER_OPTIONS,
  ...TRUE_BUNDLE_PLAN_OPTIONS,
  ...RETIRED_TRUE_BUNDLE_PLANS,
].find(
  plan => plan.feature === feature,
) ?? RETIRED_TRUE_BUNDLE_PLANS[0];

// The single billing cycle every cycle param in the URL agrees on, or undefined
// when the link pinned no cycle at all or mixed monthly and annual. A link that
// pins one cycle for one SKU still counts as agreeing.
export const getUniformCycleParam = (
  cycleParamByFeature: Partial<Record<Feature, PlanInterval>>,
): PlanInterval | undefined => {
  const cycles = Object.values(cycleParamByFeature)
    .filter((cycle): cycle is PlanInterval => cycle !== undefined);
  if (cycles.length === 0) {
    return undefined;
  }
  return cycles.every(cycle => cycle === cycles[0]) ? cycles[0] : undefined;
};
